import { useHasFeature } from 'api';
import { useTranslations } from 'i18n';
import { FrontendConfigFeaturesEnum } from 'oas';
import * as React from 'react';
import { ZendeskAPI } from 'react-zendesk';
import { IconButton, type IconButtonProps } from 'shared/icon-button';
import { useOnboarding } from 'support/onboarding';
import { IconLoader, Select, type SelectMenuItem, ThemeProvider, Tooltip } from 'ui';

const MANUALS_ID = '0';
const CONTACT_ID = '1';
const ONBOARDING_ID = '2';

const ManualsIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <IconLoader iconName="IconExternal" size="sm" {...props} />
);
const ContactIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <IconLoader iconName="IconChat" size="sm" {...props} />
);

const OnboardingIcon = ManualsIcon;

// Tooltip wraps only the trigger: wrapping the whole Select kept it open while hovering the inline menu.
// Forwards the ref and a11y props the Dropdown clones onto its trigger so they land on the button.
// Light theme keeps the tooltip matching the other header tooltips; only the menu is dark.
const HelpTrigger = React.forwardRef<HTMLButtonElement, IconButtonProps>((props, ref) => {
    const { __ } = useTranslations();
    return (
        <ThemeProvider theme="light">
            <Tooltip content={__('misc.help')}>
                <IconButton ref={ref} aria-label={__('misc.help')} {...props}>
                    <IconLoader iconName="IconHelp" />
                </IconButton>
            </Tooltip>
        </ThemeProvider>
    );
});
HelpTrigger.displayName = 'HelpTrigger';

export const SupportButton: React.FC = () => {
    const { __ } = useTranslations();
    const showCoreHelpCenter = useHasFeature(FrontendConfigFeaturesEnum.ShowCoreHelpCenter);
    const { url: onboardingUrl, open: openOnboarding } = useOnboarding();

    const openHelpCenter = React.useCallback(() => {
        const helpCenter = showCoreHelpCenter
            ? 'https://core.catapultsports.com/hc/en-us'
            : 'https://support.catapultsports.com/hc/en-us';
        window.open(helpCenter);
    }, [showCoreHelpCenter]);

    const openMessenger = React.useCallback(() => {
        ZendeskAPI('messenger', 'open');
    }, []);

    // Test ids match the referee menu they replace, so existing e2e page objects keep working
    const menuItems: SelectMenuItem[] = React.useMemo(() => {
        const items: SelectMenuItem[] = [
            {
                id: MANUALS_ID,
                testId: `group-option-${MANUALS_ID}`,
                value: __('header.help_portal'),
                isSelected: false,
                icon: ManualsIcon,
                onClick: openHelpCenter,
            },
            {
                id: CONTACT_ID,
                testId: `group-option-${CONTACT_ID}`,
                value: __('misc.contact_us'),
                isSelected: false,
                icon: ContactIcon,
                onClick: openMessenger,
            },
        ];

        // no url means Vector Core or no Zendesk user
        if (onboardingUrl) {
            items.splice(1, 0, {
                id: ONBOARDING_ID,
                testId: `group-option-${ONBOARDING_ID}`,
                value: __('header.onboarding'),
                isSelected: false,
                icon: OnboardingIcon,
                onClick: openOnboarding,
            });
        }

        return items;
    }, [__, openHelpCenter, openMessenger, onboardingUrl, openOnboarding]);

    const renderTrigger = () => <HelpTrigger />;

    return (
        <ThemeProvider theme="dark">
            <Select
                id="support-menu"
                ariaLabel={__('misc.help')}
                middleMenuItems={menuItems}
                middleMenuItemsClassName="whitespace-nowrap"
                onItemSelect={() => undefined}
                actionMenu
                displaySelectedIcon={false}
                placement="bottom"
                renderTrigger={renderTrigger}
            />
        </ThemeProvider>
    );
};
